import { useRef } from "react";

import Spinner from "components/layout/loading/Spinner";

import { ReactComponent as CameraIcon } from "assets/svgs/Camera.svg";
import { ReactComponent as CloseIcon } from "assets/svgs/Close.svg";

interface ARCThumbnailProps {
    /** undefined while loading, null when none was uploaded. */
    thumbnail: string | null | undefined;
    /** Default image shown when there is no uploaded thumbnail. */
    fallback: React.ReactNode;
    editing?: boolean;
    /** Shows a spinner overlay; independent of editing so it survives leaving edit mode mid-upload. */
    uploading?: boolean;
    onPick?: (file: File) => void;
    /** Clears the uploaded thumbnail so the default image shows again. */
    onRemove?: () => void;
}

// Square box at the default image's height; uploads are cropped to fill it.
const THUMBNAIL_BOX = "aspect-square h-[7.25rem] w-[7.25rem] rounded-r5";

// Grey veil, densest in the middle and fading to transparent at all four edges.
const UPLOAD_OVERLAY_BACKGROUND =
    "radial-gradient(closest-side, rgba(51, 51, 51, 0.75), rgba(51, 51, 51, 0.5) 60%, transparent)";

/**
 * ARC card image. Shows a placeholder while the thumbnail request is in
 * flight. In edit mode the image and a camera button both open the browser's
 * file picker.
 */
const CORNER_BUTTON =
    "absolute right-1 top-1 flex h-8 w-8 items-center justify-center rounded-full bg-gray-300 text-gray-900 shadow hover:bg-gray-200";

const ARCThumbnail = ({
    thumbnail,
    fallback,
    editing = false,
    uploading = false,
    onPick,
    onRemove,
}: ARCThumbnailProps) => {
    const fileInputRef = useRef<HTMLInputElement>(null);

    const picture =
        thumbnail === undefined ? (
            <div
                className={`${THUMBNAIL_BOX} animate-pulse bg-black70`}
                aria-label="Loading thumbnail"
            />
        ) : thumbnail ? (
            <img className={`${THUMBNAIL_BOX} object-cover`} src={thumbnail} alt="ARC thumbnail" />
        ) : (
            fallback
        );

    const image = uploading ? (
        <div className="relative">
            {picture}
            <div
                className="absolute inset-0 flex items-center justify-center"
                style={{ background: UPLOAD_OVERLAY_BACKGROUND }}
                aria-label="Uploading thumbnail"
            >
                <Spinner className="h-8 w-8" />
            </div>
        </div>
    ) : (
        picture
    );

    if (!editing) return <>{image}</>;

    const openPicker = (e: React.MouseEvent) => {
        // Keep the click from reaching the card's own onClick.
        e.stopPropagation();
        fileInputRef.current?.click();
    };

    const remove = (e: React.MouseEvent) => {
        e.stopPropagation();
        onRemove?.();
    };

    return (
        <div className="relative cursor-pointer" onClick={openPicker}>
            {image}
            {/* Uploaded thumbnail: the corner button clears it. Default image: it uploads one.
                Hidden while a change is in flight. Clicking the image itself always uploads. */}
            {!uploading &&
                (thumbnail ? (
                    <button type="button" aria-label="Remove thumbnail" className={CORNER_BUTTON} onClick={remove}>
                        <CloseIcon className="block !h-3 !w-3" />
                    </button>
                ) : (
                    <button type="button" aria-label="Upload thumbnail" className={CORNER_BUTTON} onClick={openPicker}>
                        <CameraIcon className="block !h-5 !w-5" />
                    </button>
                ))}
            <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                className="hidden"
                onClick={(e) => e.stopPropagation()}
                onChange={(e) => {
                    const file = e.target.files?.[0];
                    // Reset so picking the same file again still fires onChange.
                    e.target.value = "";
                    if (file) onPick?.(file);
                }}
            />
        </div>
    );
};

export default ARCThumbnail;
